<script setup>

import {computed, onMounted, ref, watch} from "vue";

const emit = defineEmits(['update:value', 'update:result', 'update:resultString'])

const props = defineProps({
    size: {
        type: String,
        required: false,
        default: null,
        options: ['sm', 'md',]
    },
    textAlignment: {
        type: String,
        required: false,
        default: 'center',
    },
    widgetUuid: {
        type: String,
        required: true,
    }
})

const widget = ref(null)
const calculating = ref(false)
const showResult = ref(false)
const result = ref(null)
const inputValues = ref({})

const isVersion2 = computed(() => widget.value?.version === 2)

const loadWidget = async (widgetUuid) => {
    const res = await axios.get(route('api.widget.show', widgetUuid))
    widget.value = res.data.widget

    Object.values(res.data.widget.inputs).forEach((field) => {
        inputValues.value[field.name] = {name: field.name, value: field.defaultValue}
    })
}

const calculateResult = async () => {
    calculating.value = true
    const res = await axios.post(route('api.widget.calculate', props.widgetUuid), {
        inputValues: inputValues.value
    })
    result.value = res.data

    if (isVersion2.value) {
        emit('update:value', result.value.displayText)
        emit('update:result', result.value)
        emit('update:resultString', result.value.displayText)
    } else {
        emit('update:value', res.data.result)
        emit('update:result', result.value)
        const resultString = `${result.value.title} ${result.value.prefix} ${result.value.result} ${result.value.suffix} ${result.value.byline}`
        emit('update:resultString', resultString)
    }

    showResult.value = true
    calculating.value = false
}

const clear = () => {
    showResult.value = false
    result.value = null
}

const cardSizeClass = computed(() => {
    const sizes = {
        sm: 'p-4 ',
        md: 'p-8',
    }
    return sizes[props.size] ?? null
})

const buttonSizeClass = computed(() => {
    const sizes = {
        sm: 'sm',
        md: 'md',
    }
    return sizes[props.size]
})

const copyableResult = computed(() => {
    if (!isVersion2.value || !result.value) {
        return null
    }

    if (result.value.displayText) {
        return result.value.displayText
    }

    return null
})

watch(() => props.widgetUuid, () => {
    loadWidget(props.widgetUuid)
})

onMounted(() => {
    loadWidget(props.widgetUuid)
})

</script>

<template>
    <div
        v-if="widget"
        class="rounded-wl"
        :class="cardSizeClass"
    >
        <div>
            <div v-if="! calculating && ! showResult">
                <div>
                    <div
                        v-for="(input, i) in widget.inputs"
                        :key="i"
                    >
                        <input-label
                            :label="input.label"
                            align="center"
                        ></input-label>
                        <p
                            v-if="input.description"
                            class="text-xs !mb-1 text-center"
                        >{{ input.description }}</p>
                        <input-number
                            input-class="text-center text-black"
                            v-model:content="inputValues[input.name].value"
                        ></input-number>
                        <input-range
                            v-if="input.inputType === 'slider'"
                            v-model:content="inputValues[input.name].value"
                            :min="input.min"
                            :max="input.max"
                            :step="input.step"
                            class="mb-8"
                        />
                    </div>
                </div>
                <div class="flex justify-center">
                    <button-primary
                        :size="buttonSizeClass"
                        :disabled="!Object.keys(widget.inputs).length"
                        @click="calculateResult"
                        class="w-full justify-center max-w-24"
                    >
                        {{ widget.buttonLabels.calculate }}
                    </button-primary>
                </div>
            </div>
            <div v-if="showResult">
                <!-- Version 1 Result Display -->
                <div v-if="!isVersion2">
                    <div class="text-center">
                        <p
                            v-if="result.title"
                            class="text-lg mb-0"
                        >
                            {{ result.title }}
                        </p>
                        <div class="text-xl font-bold">
                            {{ result.prefix}}
                            {{ result.result }}
                            {{ result.suffix}}
                        </div>
                        <p
                            v-if="result.byline"
                        >
                            {{ result.byline }}
                        </p>
                    </div>
                </div>

                <!-- Version 2 Result Display -->
                <div v-else>
                    <div class="text-center">
                        <p
                            v-if="result.title"
                            class="text-lg mb-2"
                        >
                            {{ result.title }}
                        </p>
                        <div v-if="result.displayText && result.displayText.trim()" class="text-lg mb-4 whitespace-pre-line">
                            {{ result.displayText }}
                        </div>
                        <div v-else class="text-lg mb-4">
                            <!-- Fallback display when no template is configured -->
                            <div v-if="result.results && Object.keys(result.results).length" class="space-y-2">
                                <div v-for="(value, name) in result.results" :key="name" class="flex justify-center">
                                    <span class="capitalize">{{ name.replace(/_/g, ' ') }}:</span>
                                    <span class="ml-2 font-bold">{{ value }}</span>
                                </div>
                            </div>
                            <div v-else class="text-gray-500">
                                No results to display
                            </div>
                        </div>
                        <p
                            v-if="result.byline"
                            class="text-sm text-gray-600"
                        >
                            {{ result.byline }}
                        </p>
                    </div>
                </div>

                <div
                    v-if="copyableResult"
                    class="flex justify-center mt-4"
                >
                    <copyable-text
                        :show-url="false"
                        :copyable-text="copyableResult"
                    />
                </div>

                <div class="flex justify-center mt-4">
                    <button-base
                        :size="buttonSizeClass"
                        @click="clear"
                        class="text-black"
                    >
                        {{ widget.buttonLabels.back }}
                    </button-base>
                </div>
            </div>
        </div>
    </div>
    <div v-else><mf-spinner-medium class="w-12 h-6"></mf-spinner-medium></div>
</template>

<style scoped>

</style>
